import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';

function requireDb() {
  if (!db) {
    throw new Error('Firestore is not configured. Add REACT_APP_FIREBASE_* values to .env.local.');
  }
}

function runsRef(uid) {
  return collection(db, 'componentRuns', uid, 'runs');
}

function runRef(uid, runId) {
  return doc(db, 'componentRuns', uid, 'runs', runId);
}

function timestampValue(value) {
  if (!value) {
    return 0;
  }
  if (typeof value.toMillis === 'function') {
    return value.toMillis();
  }
  return Number(new Date(value)) || 0;
}

function sortRuns(items) {
  return [...items].sort((a, b) => {
    if (a.runDate !== b.runDate) {
      return String(b.runDate || '').localeCompare(String(a.runDate || ''));
    }
    return timestampValue(b.updatedAt || b.createdAt) - timestampValue(a.updatedAt || a.createdAt);
  });
}

export function todayRunDate(date = new Date()) {
  return date.toISOString().slice(0, 10);
}

async function isFriendWithOwner(ownerId, viewerUid) {
  if (!viewerUid || viewerUid === ownerId) {
    return false;
  }

  requireDb();
  const snapshot = await getDoc(doc(db, 'profileFriends', ownerId, 'friends', viewerUid));
  return snapshot.exists();
}

export async function listComponentRuns(uid) {
  requireDb();
  const snapshot = await getDocs(runsRef(uid));
  return sortRuns(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
}

export async function listRunsForComponent(uid, componentId) {
  requireDb();
  const snapshot = await getDocs(query(runsRef(uid), where('componentId', '==', componentId)));
  return sortRuns(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
}

export async function listVisibleComponentRuns(uid, viewerUid = null) {
  requireDb();
  if (viewerUid === uid) {
    return listComponentRuns(uid);
  }

  const visibilityQuery = await isFriendWithOwner(uid, viewerUid)
    ? query(runsRef(uid), where('visibility', 'in', ['public', 'friends']))
    : query(runsRef(uid), where('visibility', '==', 'public'));
  const snapshot = await getDocs(visibilityQuery);
  return sortRuns(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
}

export async function createComponentRun(uid, component, input) {
  requireDb();
  const now = serverTimestamp();
  const ref = await addDoc(runsRef(uid), {
    ownerId: uid,
    componentId: component.id,
    templateId: component.templateId,
    kind: component.kind,
    runDate: input.runDate || todayRunDate(),
    status: input.status || 'completed',
    response: input.response || {},
    score: Number(input.score) || 0,
    durationSeconds: Number(input.durationSeconds) || 0,
    visibility: input.visibility || component.visibility || 'private',
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

export async function updateComponentRun(uid, runId, run) {
  requireDb();
  await updateDoc(runRef(uid, runId), {
    runDate: run.runDate,
    status: run.status,
    response: run.response || {},
    score: Number(run.score) || 0,
    durationSeconds: Number(run.durationSeconds) || 0,
    visibility: run.visibility,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteComponentRun(uid, runId) {
  requireDb();
  await deleteDoc(runRef(uid, runId));
}

export function summarizeRuns(runs) {
  const completedRuns = runs.filter((run) => run.status === 'completed');
  const lastCompleted = completedRuns[0]?.runDate || '';
  return {
    total: runs.length,
    completed: completedRuns.length,
    skipped: runs.filter((run) => run.status === 'skipped').length,
    lastCompleted,
  };
}

function daysBetween(fromDate, toDate) {
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const from = Date.UTC(fromDate.getUTCFullYear(), fromDate.getUTCMonth(), fromDate.getUTCDate());
  const to = Date.UTC(toDate.getUTCFullYear(), toDate.getUTCMonth(), toDate.getUTCDate());
  return Math.floor((to - from) / millisecondsPerDay);
}

export function dailyStatsForComponent(runs, componentId, now = new Date()) {
  const scopedRuns = runs.filter((run) => run.componentId === componentId);
  const stats = {
    day: 0,
    week: 0,
    month: 0,
    year: 0,
    allTime: scopedRuns.length,
    completed: 0,
    skipped: 0,
    latestRunDate: scopedRuns[0]?.runDate || '',
  };

  scopedRuns.forEach((run) => {
    if (run.status === 'completed') stats.completed += 1;
    if (run.status === 'skipped') stats.skipped += 1;

    const runDate = new Date(`${run.runDate}T00:00:00Z`);
    const ageDays = daysBetween(runDate, now);
    if (ageDays === 0) stats.day += 1;
    if (ageDays >= 0 && ageDays < 7) stats.week += 1;
    if (ageDays >= 0 && ageDays < 31) stats.month += 1;
    if (ageDays >= 0 && ageDays < 366) stats.year += 1;
  });

  return stats;
}
